import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Table, 
  Tooltip, 
  Pagination, 
  Switch, 
  Select, 
  Card, 
  message,
  Row,
  Col,
  InputNumber,
  Space,
  Tag
} from 'antd';
import { ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css'; // 仅为此页面导入 Ant Design 样式
import API from '../services/api';

const AllServersAttackQuery = () => {
  const [attackLogs, setAttackLogs] = useState([]); // 存储所有服务器的攻击日志
  const [filteredLogs, setFilteredLogs] = useState([]); // 筛选后的日志
  const [loading, setLoading] = useState(false); // 控制加载动画
  const [currentPage, setCurrentPage] = useState(1); // 当前页码
  const logsPerPage = 10; // 每页显示的日志数量
  const [autoRefresh, setAutoRefresh] = useState(false); // 自动刷新状态
  const [refreshInterval, setRefreshInterval] = useState(60); // 刷新间隔（秒）
  
  // 筛选条件状态
  const [filters, setFilters] = useState({
    minTraffic: null, // 最小峰值流量 (Gbps)
    maxTraffic: null, // 最大峰值流量 (Gbps)
    status: 'all', // 攻击状态: all, ongoing, ended
    packetType: 'all', // 包类型: all, TCP, UDP, ICMP 等
    minInterceptRate: null, // 最小拦截率 (%)
    maxInterceptRate: null, // 最大拦截率 (%)
  });

  // 获取所有服务器的攻击日志
  const fetchAllAttackLogs = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/admin/attack-lookup-all');
      const logs = response.data.attackLogs || [];
      setAttackLogs(logs);
      setCurrentPage(1); // 重置到第一页
      if (logs.length > 0) {
        message.success(`获取成功，共 ${logs.length} 条攻击记录`);
      }
    } catch (error) {
      console.error('获取攻击日志时出错:', error);
      
      // 直接显示后端返回的错误内容
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.request) {
        message.error('网络连接失败，请检查网络状态');
      } else {
        message.error('获取攻击日志时出错，请稍后重试');
      }
    }
    setLoading(false);
  };

  // 筛选数据
  const filterLogs = (logs, filterConditions) => {
    return logs.filter(log => {
      const trafficGbps = convertToGbps(log.size);
      const interceptRate = parseFloat(calculateInterceptRate(log.size, log.drop_size).replace('%', ''));
      
      // 峰值流量筛选
      if (filterConditions.minTraffic !== null && trafficGbps < filterConditions.minTraffic) {
        return false;
      }
      if (filterConditions.maxTraffic !== null && trafficGbps > filterConditions.maxTraffic) {
        return false;
      }
      
      // 攻击状态筛选
      if (filterConditions.status !== 'all') {
        if (filterConditions.status === 'ongoing' && log.status !== 1) {
          return false;
        }
        if (filterConditions.status === 'ended' && log.status !== 0) {
          return false;
        }
      }
      
      // 包类型筛选
      if (filterConditions.packetType !== 'all' && log.packet_type !== filterConditions.packetType) {
        return false;
      }
      
      // 拦截率筛选
      if (filterConditions.minInterceptRate !== null && interceptRate < filterConditions.minInterceptRate) {
        return false;
      }
      if (filterConditions.maxInterceptRate !== null && interceptRate > filterConditions.maxInterceptRate) {
        return false;
      }
      
      return true;
    });
  };

  // 应用筛选
  const applyFilters = () => {
    const filtered = filterLogs(attackLogs, filters);
    setFilteredLogs(filtered);
    setCurrentPage(1);
    message.success(`筛选完成，共 ${filtered.length} 条记录符合条件`);
  };

  // 清除筛选
  const clearFilters = () => {
    setFilters({
      minTraffic: null,
      maxTraffic: null,
      status: 'all',
      packetType: 'all',
      minInterceptRate: null,
      maxInterceptRate: null,
    });
    setFilteredLogs([]);
    setCurrentPage(1);
    message.success('已清除所有筛选条件');
  };

  // 获取包类型选项
  const getPacketTypeOptions = () => {
    const types = [...new Set(attackLogs.map(log => log.packet_type).filter(Boolean))];
    return types.map(type => ({ label: type, value: type }));
  };

  // 组件加载时获取数据
  useEffect(() => {
    fetchAllAttackLogs();
  }, []);

  // 自动刷新逻辑
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(fetchAllAttackLogs, refreshInterval * 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, refreshInterval]);

  const convertDurationToChineseWithSeconds = (duration) => {
    const match = duration.match(/(\d+)(seconds?|minutes?|hours?)/g);
    let result = '';
    if (!match) return duration;
  
    match.forEach((time) => {
      const [value, unit] = time.match(/\d+|[a-zA-Z]+/g);
      switch (unit) {
        case 'second':
        case 'seconds':
          result += `${value}秒`;
          break;
        case 'minute':
        case 'minutes':
          result += `${value}分钟 `;
          break;
        case 'hour':
        case 'hours':
          result += `${value}小时 `;
          break;
        default:
          result += time;
      }
    });
  
    return result.trim();
  };

  const convertToGbps = (sizeInMbps) => {
    return (sizeInMbps / 1000).toFixed(2);
  };

  const formatDateTimeWithoutMilliseconds = (dateTimeStr) => {
    return dateTimeStr.split('.')[0];
  };

  const convertToGbpsFromBytes = (sizeInBytes) => {
    const sizeInBits = sizeInBytes * 8;
    const sizeInMbps = sizeInBits / 1e6;
    return (sizeInMbps / 1000).toFixed(2);
  };

  const calculateInterceptRate = (size, dropSize) => {
    const peakTrafficGbps = convertToGbps(size);
    const dropTrafficGbps = convertToGbpsFromBytes(dropSize);
    
    if (peakTrafficGbps === 0) return '0.00%';
    
    let interceptRate = (dropTrafficGbps / peakTrafficGbps) * 100;
    interceptRate = interceptRate.toString();
    const dotIndex = interceptRate.indexOf('.');
    
    if (dotIndex !== -1) {
      interceptRate = interceptRate.substring(0, dotIndex + 3);
    } else {
      interceptRate = interceptRate + '.00';
    }
    
    return `${interceptRate}%`;
  };

  // 分页逻辑 - 使用筛选后的数据或原始数据
  const displayLogs = filteredLogs.length > 0 || Object.values(filters).some(v => v !== null && v !== 'all') ? filteredLogs : attackLogs;
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = displayLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(displayLogs.length / logsPerPage);

  // 判断是否有筛选条件生效
  const hasActiveFilters = Object.values(filters).some(v => v !== null && v !== 'all');

  // 表格列定义
  const columns = [
    {
      title: '目标IP',
      dataIndex: 's_ip',
      key: 's_ip',
      width: 130,
      align: 'center',
    },
    {
      title: '目的端口',
      dataIndex: 's_port_list',
      key: 's_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '攻击源IP',
      dataIndex: 'c_ip_list',
      key: 'c_ip_list',
      width: 130,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 15 ? `${text.slice(0, 12)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '源端口',
      dataIndex: 'c_port_list',
      key: 'c_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (status) => (
        <span 
          style={{ 
            color: status === 1 ? '#ff4d4f' : '#52c41a',
            fontWeight: 500
          }}
        >
          {status === 1 ? '正在攻击' : '攻击结束'}
        </span>
      ),
    },
    {
      title: '防护方式',
      dataIndex: 'deftype',
      key: 'deftype',
      width: 220,
      align: 'center',
    },
    {
      title: '峰值流量',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      align: 'center',
      render: (size) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>
          {convertToGbps(size)} Gbps
        </span>
      ),
    },
    {
      title: '拦截流量',
      dataIndex: 'drop_size',
      key: 'drop_size',
      width: 120,
      align: 'center',
      render: (dropSize) => (
        <span style={{ fontWeight: 500, color: '#52c41a' }}>
          {convertToGbpsFromBytes(dropSize)} Gbps
        </span>
      ),
    },
    {
      title: '拦截率',
      key: 'intercept_rate',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const rate = calculateInterceptRate(record.size, record.drop_size);
        const percentage = parseFloat(rate.replace('%', ''));
        const color = percentage > 80 ? '#52c41a' : percentage > 50 ? '#faad14' : '#ff4d4f';
        return (
          <span style={{ fontWeight: 500, color }}>
            {rate}
          </span>
        );
      },
    },
    {
      title: '包类型',
      dataIndex: 'packet_type',
      key: 'packet_type',
      width: 90,
      align: 'center',
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 160,
      align: 'center',
      render: (time) => formatDateTimeWithoutMilliseconds(time),
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 160,
      align: 'center',
    },
    {
      title: '持续时间',
      dataIndex: 'duration',
      key: 'duration',
      width: 130,
      align: 'center',
      render: (duration) => (
        <span style={{ fontWeight: 500 }}>
          {convertDurationToChineseWithSeconds(duration)}
        </span>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#fafafa', 
      minHeight: 'calc(100vh - 64px)'
    }}>
      <div style={{ maxWidth: '100%', margin: '0 auto' }}>
        {/* 页面标题 */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          background: '#fff',
          padding: '24px 20px',
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: '24px', 
            fontWeight: '500',
            color: '#262626'
          }}>
            近24小时攻击记录
          </h1>
          <p style={{ 
            margin: '8px 0 0 0', 
            fontSize: '14px', 
            color: '#8c8c8c'
          }}>
            DDoS防护系统 - 实时监控
          </p>
        </div>
        
        {/* 控制区域 */}
        <Card 
          style={{ 
            marginBottom: '20px',
            border: '1px solid #e8e8e8'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchAllAttackLogs}
              loading={loading}
              size="large"
            >
              刷新数据
            </Button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {autoRefresh ? (
                <PauseCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
              ) : (
                <PlayCircleOutlined style={{ color: '#8c8c8c', fontSize: '16px' }} />
              )}
              <span style={{ color: '#595959', fontSize: '14px' }}>自动刷新</span>
              <Switch
                checked={autoRefresh}
                onChange={setAutoRefresh}
              />
            </div>
            
            {autoRefresh && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#595959', fontSize: '14px' }}>刷新间隔:</span>
                <Select
                  value={refreshInterval}
                  onChange={setRefreshInterval}
                  style={{ width: '100px' }}
                >
                  <Select.Option value={30}>30秒</Select.Option>
                  <Select.Option value={60}>1分钟</Select.Option>
                  <Select.Option value={300}>5分钟</Select.Option>
                  <Select.Option value={600}>10分钟</Select.Option>
                </Select>
              </div>
            )}
          </div>
        </Card>

        {/* 筛选区域 */}
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FilterOutlined />
              <span>数据筛选</span>
              {hasActiveFilters && (
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  筛选中
                </Tag>
              )}
            </div>
          }
          style={{ 
            marginBottom: '20px',
            border: '1px solid #e8e8e8'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#595959', fontSize: '14px', fontWeight: 500 }}>峰值流量范围 (Gbps)</span>
              </div>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  placeholder="最小值"
                  value={filters.minTraffic}
                  onChange={(value) => setFilters({...filters, minTraffic: value})}
                  min={0}
                  precision={2}
                  style={{ width: '50%' }}
                />
                <InputNumber
                  placeholder="最大值"
                  value={filters.maxTraffic}
                  onChange={(value) => setFilters({...filters, maxTraffic: value})}
                  min={0}
                  precision={2}
                  style={{ width: '50%' }}
                />
              </Space.Compact>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#595959', fontSize: '14px', fontWeight: 500 }}>拦截率范围 (%)</span>
              </div>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  placeholder="最小值"
                  value={filters.minInterceptRate}
                  onChange={(value) => setFilters({...filters, minInterceptRate: value})}
                  min={0}
                  max={100}
                  precision={2}
                  style={{ width: '50%' }}
                />
                <InputNumber
                  placeholder="最大值"
                  value={filters.maxInterceptRate}
                  onChange={(value) => setFilters({...filters, maxInterceptRate: value})}
                  min={0}
                  max={100}
                  precision={2}
                  style={{ width: '50%' }}
                />
              </Space.Compact>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#595959', fontSize: '14px', fontWeight: 500 }}>攻击状态</span>
              </div>
              <Select
                value={filters.status}
                onChange={(value) => setFilters({...filters, status: value})}
                style={{ width: '100%' }}
                options={[
                  { label: '全部状态', value: 'all' },
                  { label: '正在攻击', value: 'ongoing' },
                  { label: '攻击结束', value: 'ended' },
                ]}
              />
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#595959', fontSize: '14px', fontWeight: 500 }}>包类型</span>
              </div>
              <Select
                value={filters.packetType}
                onChange={(value) => setFilters({...filters, packetType: value})}
                style={{ width: '100%' }}
                options={[
                  { label: '全部类型', value: 'all' },
                  ...getPacketTypeOptions()
                ]}
              />
            </Col>
          </Row>
          
          <div style={{ 
            marginTop: '20px', 
            textAlign: 'center',
            paddingTop: '16px',
            borderTop: '1px solid #f0f0f0'
          }}>
            <Space>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={applyFilters}
                disabled={attackLogs.length === 0}
              >
                应用筛选
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={clearFilters}
                disabled={!hasActiveFilters}
              >
                清除筛选
              </Button>
            </Space>
          </div>
        </Card>

        {/* 数据统计 */}
        {attackLogs.length > 0 && (
          <Card
            style={{ 
              marginBottom: '20px',
              border: '1px solid #e8e8e8'
            }}
            bodyStyle={{ padding: '16px 24px' }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: '#f5f5f5',
              padding: '12px 16px',
              borderRadius: '6px',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <div style={{ 
                color: '#595959', 
                fontSize: '14px'
              }}>
                {hasActiveFilters ? (
                  <>
                    筛选结果: <strong style={{ color: '#1890ff' }}>{displayLogs.length}</strong> 条记录
                    （总计 <strong>{attackLogs.length}</strong> 条），
                    当前显示第 <strong>{indexOfFirstLog + 1}</strong> - <strong>{Math.min(indexOfLastLog, displayLogs.length)}</strong> 条
                  </>
                ) : (
                  <>
                    总计 <strong>{attackLogs.length}</strong> 条记录，
                    当前显示第 <strong>{indexOfFirstLog + 1}</strong> - <strong>{Math.min(indexOfLastLog, attackLogs.length)}</strong> 条
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {hasActiveFilters && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    <FilterOutlined style={{ marginRight: '4px' }} />
                    已筛选
                  </Tag>
                )}
                {autoRefresh && (
                  <div style={{ 
                    color: '#52c41a', 
                    fontSize: '12px',
                    background: '#f6ffed',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #b7eb8f'
                  }}>
                    ● 自动刷新中 ({refreshInterval}秒)
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* 表格区域 */}
        <Card
          style={{ border: '1px solid #e8e8e8' }}
          bodyStyle={{ padding: '20px' }}
        >
          {attackLogs.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '50px 20px', 
              color: '#8c8c8c',
              background: '#fafafa',
              borderRadius: '6px',
              border: '1px dashed #d9d9d9'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }}>📊</div>
              <div style={{ fontSize: '16px', marginBottom: '8px', color: '#595959' }}>
                暂无攻击记录
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                当前24小时内没有检测到攻击活动
              </div>
            </div>
          ) : displayLogs.length === 0 && hasActiveFilters ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '50px 20px', 
              color: '#8c8c8c',
              background: '#fafafa',
              borderRadius: '6px',
              border: '1px dashed #d9d9d9'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }}>🔍</div>
              <div style={{ fontSize: '16px', marginBottom: '8px', color: '#595959' }}>
                没有符合条件的记录
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                请尝试调整筛选条件或清除筛选重新查看
              </div>
              <Button 
                type="link" 
                onClick={clearFilters}
                style={{ marginTop: '12px' }}
              >
                清除筛选条件
              </Button>
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                dataSource={currentLogs}
                pagination={false}
                scroll={{ x: 1800 }}
                size="middle"
                bordered
                rowKey={(record, index) => index}
                style={{ borderRadius: '6px', overflow: 'hidden' }}
                loading={loading}
              />
              
              {totalPages > 1 && (
                <div style={{ 
                  marginTop: '20px', 
                  textAlign: 'center',
                  padding: '16px',
                  background: '#fafafa',
                  borderRadius: '6px'
                }}>
                  <Pagination
                    current={currentPage}
                    total={displayLogs.length}
                    pageSize={logsPerPage}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showQuickJumper
                    showTotal={(total, range) => 
                      `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录${hasActiveFilters ? ' (已筛选)' : ''}`
                    }
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </div>
      
      {/* 简化的自定义样式 */}
      <style jsx>{`
        .ant-table-thead > tr > th {
          background-color: #fafafa;
          color: #262626;
          font-weight: 500;
          border-bottom: 2px solid #e8e8e8;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #f5f5f5;
        }
        .ant-table-tbody > tr:nth-child(even) {
          background-color: #fdfdfd;
        }
      `}</style>
    </div>
  );
};

export default AllServersAttackQuery; 